import { TextButton } from '@oneaudi/unified-web-components';
import { useAppServices, useAppState } from '~/app/contexts/app-context';
import { useDialogs } from '~/app/hooks/use-dialogs';
import { i18nKeys, useI18n } from '~/app/i18n/i18n';
import { useSaveConfigurationNotification } from '~/app/pages/components/notification/use-notification';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';

export const SaveConfiguration = () => {
	const { i18n } = useI18n();
	const { isLoggedIn, accessToken, audiCode } = useAppState();
	const {
		featureServices: { authService },
	} = useAppServices();
	const { closeDialog } = useDialogs();
	const labelText = isLoggedIn
		? i18n('ngc.saveConfiguration.myAudi.save')
		: i18n('ngc.saveConfiguration.myAudi.login');
	const audiCodeString = audiCode?.data.id;
	const { showNotification } = useSaveConfigurationNotification();

	const saveOrLogin = async () => {
		if (audiCodeString) {
			if (!isLoggedIn) {
				const url = new URL(window.document.location.href);
				url.searchParams.set('saveconfig', audiCodeString);

				appTrackingService.summaryClickEvent({
					eventAction: EventAction.ExitLink,
					eventName: `configurator (save configuration) - click on login myAudi`,
					componentName: 'configurator-save-configuration',
					label: labelText,
					elementName: 'button',
					targetURL: `${url}`,
				});

				await authService.login(undefined, typeof window !== 'undefined' ? url.toString() : undefined);
			} else {
				appTrackingService.summaryClickEvent({
					eventAction: EventAction.InternalLink,
					eventName: `configurator (save configuration) - click on save to my wishlist`,
					componentName: 'configurator-save-configuration',
					label: labelText,
					elementName: 'button',
				});

				const result = await saveAudicode(audiCodeString, accessToken);

				appTrackingService.saveConfiguration({
					eventName: `configurator (save configuration) - save configuration ${result.isError ? 'error' : 'success'} message`,
					componentName: 'configurator-save-configuration',
				});
				showNotification((state) => ({ ...state, ...result }));
				closeDialog('audiCode');
			}
		}
	};

	return (
		<div>
			<TextButton
				data-testid="save-configuration"
				variant="primary"
				isLoading={!audiCode || !audiCodeString}
				onClick={saveOrLogin}
				size="md"
				id="login">
				{labelText}
			</TextButton>
		</div>
	);
};

export const saveAudicode = async (audiCode: string | undefined, accessToken: string | undefined) => {
	const apiBaseUrl = 'https://fa-ngc-one-pager.api.prod.arcade.apps.one.audi/api';
	try {
		const response = await fetch(`${apiBaseUrl}/add?audicode=${audiCode}`, {
			method: 'PUT',
			headers: {
				authorization: `Bearer ${accessToken}`,
			},
			credentials: 'omit',
		});
		if (response.status === 410) {
			return { message: 'ngc.saveConfiguration.noSpaceError.message' as keyof typeof i18nKeys, isError: true };
		}
		if (response.ok) {
			return { message: 'ngc.saveConfiguration.success.message' as keyof typeof i18nKeys, isError: false };
		}
		console.error('Unexpected response status:', response.status);
		return { message: 'ngc.saveConfiguration.generalError.message' as keyof typeof i18nKeys, isError: true };
	} catch (e) {
		console.error('Error while saving audiCode:', e);
		return { message: 'ngc.saveConfiguration.generalError.message' as keyof typeof i18nKeys, isError: true };
	}
};
